import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import Nav from "../components/Nav";
import Logo from "../components/Logo";
import InviteCard from "../components/InviteCard";
import RealityEditor from "../components/admin/RealityEditor";
import PreRegistrations from "../components/admin/PreRegistrations";
import { DEFAULT_CONTENT, mergeContent, getCreators } from "../lib/defaults";
import { useContent } from "../lib/ContentContext";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [codes, setCodes] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("codes");
  const [newLabel, setNewLabel] = useState("");
  const [newCode, setNewCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [inviteFor, setInviteFor] = useState(null); // {code, label}

  const loadAll = async () => {
    try {
      const [c, r, s] = await Promise.all([
        api.get("/admin/codes"),
        api.get("/admin/rsvps"),
        api.get("/admin/stats"),
      ]);
      setCodes(c.data);
      setRsvps(r.data);
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setMe(r.data);
        loadAll();
      })
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) {
      /* ignore */
    }
    localStorage.removeItem("alta_admin_token");
    navigate("/admin/login");
  };

  const createCode = async (e) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    setCreating(true);
    try {
      await api.post("/admin/codes", {
        label: newLabel.trim(),
        code: newCode.trim() || undefined,
      });
      setNewLabel("");
      setNewCode("");
      toast.success("Código criado");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const deleteCode = async (id) => {
    if (!window.confirm("Excluir este código e RSVP associado?")) return;
    try {
      await api.delete(`/admin/codes/${id}`);
      toast.success("Código removido");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(`Copiado: ${text}`);
  };

  const downloadTermoPdf = async (rsvpId, code) => {
    try {
      const token = localStorage.getItem("alta_admin_token");
      const url = `${api.defaults.baseURL}/admin/rsvps/${rsvpId}/termo.pdf`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("Falha ao gerar PDF");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `casa-alta-termo-${code}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("PDF baixado");
    } catch (err) {
      toast.error("Não foi possível baixar o PDF");
    }
  };

  if (!me) return null;

  const confirmedCount = codes.filter((c) => c.used).length;
  const activatedCount = codes.filter((c) => c.activated_at).length;

  const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return "—"; }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="admin-dashboard">
      <Nav variant="admin" />

      {/* Invite modal */}
      {inviteFor && (
        <InviteCard
          code={inviteFor.code}
          label={inviteFor.label}
          onClose={() => setInviteFor(null)}
        />
      )}

      {/* Header */}
      <section className="pt-32 pb-16 border-b border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <Logo variant="dark" size={54} testid="admin-alta-logo" className="mb-6" />
            <div className="eyebrow">Painel interno</div>
            <h1 className="mt-4 serif text-[44px] md:text-[64px] leading-[1] tracking-[-0.01em]">
              Curadoria da<br />
              <em className="italic text-[color:var(--alta-red)]">Shambala House</em>
            </h1>
          </div>
          <div className="md:col-span-4 flex flex-col md:items-end gap-3">
            <div className="mono text-[10px] uppercase tracking-[0.28em] text-neutral-500">
              {me.email}
            </div>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="btn-ghost !py-3 !px-6"
            >
              Sair
            </button>
          </div>
        </div>
        {/* Stats — funil */}
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 mt-16 grid grid-cols-2 md:grid-cols-4 gap-10">
          <Stat k={codes.length} v="Convites enviados" testid="stat-enviados" />
          <Stat k={activatedCount} v="Convites ativados" tone="soft" testid="stat-ativados" />
          <Stat k={confirmedCount} v="Presenças confirmadas" tone="red" testid="stat-confirmados" />
          <Stat k={rsvps.length} v="Termos assinados" tone="red" testid="stat-termos" />
        </div>
      </section>

      {/* Tabs */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 mt-10 flex items-center gap-8 border-b border-neutral-200">
        {["codes", "rsvps", "prereg", "content"].map((t) => (
          <button
            key={t}
            data-testid={`tab-${t}`}
            onClick={() => setTab(t)}
            className={`py-4 mono text-[11px] uppercase tracking-[0.28em] border-b-2 transition-colors ${
              tab === t
                ? "border-neutral-900 text-neutral-900"
                : "border-transparent text-neutral-500 hover:text-neutral-800"
            }`}
          >
            {t === "codes"
              ? "Códigos de convidado"
              : t === "rsvps"
              ? "Respostas RSVP"
              : t === "prereg"
              ? `Pré-lista Casa Alta 02${stats?.pre_registrations ? ` · ${stats.pre_registrations}` : ""}`
              : "Textos do site"}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
        {tab === "codes" && (
          <>
            <form
              onSubmit={createCode}
              className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end border border-neutral-300 p-6 md:p-8 bg-white/50"
              data-testid="create-code-form"
            >
              <div className="md:col-span-5">
                <label className="mono text-[10px] uppercase tracking-[0.28em] text-neutral-500">
                  Nome da convidada
                </label>
                <input
                  data-testid="new-code-label"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Ex: Maria Silva (aparece no convite e na página dela)"
                  className="field-line mt-3"
                  required
                />
              </div>
              <div className="md:col-span-4">
                <label className="mono text-[10px] uppercase tracking-[0.28em] text-neutral-500">
                  Código (opcional — gerado automaticamente)
                </label>
                <input
                  data-testid="new-code-code"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                  placeholder="XXXXXXXX"
                  className="field-line mt-3 uppercase tracking-[0.14em]"
                />
              </div>
              <div className="md:col-span-3 flex md:justify-end">
                <button
                  data-testid="create-code-submit"
                  type="submit"
                  disabled={creating}
                  className="btn-sharp w-full md:w-auto"
                >
                  {creating ? "Criando..." : "Gerar código"}
                </button>
              </div>
            </form>

            <div className="mt-14">
              <table className="w-full text-left" data-testid="codes-table">
                <thead>
                  <tr className="border-b border-neutral-300 mono text-[10px] uppercase tracking-[0.24em] text-neutral-500">
                    <th className="py-4">Código</th>
                    <th className="py-4">Convidada</th>
                    <th className="py-4">Funil</th>
                    <th className="py-4">Ativado em</th>
                    <th className="py-4 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((c) => (
                    <tr
                      key={c.id}
                      className="border-b border-neutral-200 hover:bg-[#F3F1EA] transition-colors"
                      data-testid={`code-row-${c.code}`}
                    >
                      <td className="py-5 serif text-[22px] text-neutral-900 tracking-[0.1em]">
                        {c.code}
                      </td>
                      <td className="py-5 text-[14px] text-neutral-700">{c.label}</td>
                      <td className="py-5">
                        <FunnelBadge activated={!!c.activated_at} confirmed={!!c.used} />
                      </td>
                      <td className="py-5 mono text-[11px] text-neutral-500">
                        {fmtDateTime(c.activated_at)}
                      </td>
                      <td className="py-5 text-right">
                        <div className="inline-flex items-center gap-5">
                          <button
                            onClick={() => setInviteFor({ code: c.code, label: c.label })}
                            data-testid={`code-invite-${c.code}`}
                            className="mono text-[10px] uppercase tracking-[0.24em] text-[color:var(--alta-red)] hover:text-[color:var(--alta-red-dark)]"
                          >
                            Gerar convite
                          </button>
                          <button
                            onClick={() => copy(c.code)}
                            className="mono text-[10px] uppercase tracking-[0.24em] text-neutral-700 hover:text-neutral-900"
                            data-testid={`code-copy-${c.code}`}
                          >
                            Copiar
                          </button>
                          <button
                            onClick={() => deleteCode(c.id)}
                            className="mono text-[10px] uppercase tracking-[0.24em] text-neutral-500 hover:text-red-700"
                            data-testid={`code-delete-${c.code}`}
                          >
                            Excluir
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!codes.length && (
                    <tr>
                      <td colSpan={5} className="py-16 text-center text-neutral-500 font-light">
                        Nenhum código criado ainda.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "rsvps" && (
          <div className="space-y-6" data-testid="rsvps-list">
            {rsvps.map((r) => (
              <article
                key={r.id}
                className="border border-neutral-200 bg-white/60 p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-6"
                data-testid={`rsvp-item-${r.code}`}
              >
                <div className="md:col-span-4">
                  <div className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--alta-red)]">
                    {r.code}
                  </div>
                  <div className="mt-2 serif text-[26px] leading-tight text-neutral-900">
                    {r.full_name}
                  </div>
                  <div className="mt-2 text-[13px] text-neutral-600">{r.social_handle}</div>
                  {r.signature_name && (
                    <div className="mt-4 border-t border-neutral-200 pt-3">
                      <div className="mono text-[9px] uppercase tracking-[0.24em] text-neutral-500">
                        Termo assinado por
                      </div>
                      <div className="mt-1 serif italic text-[16px] text-neutral-800">
                        {r.signature_name}
                      </div>
                      <div className="mt-1 mono text-[9px] text-neutral-500">
                        {fmtDateTime(r.signed_at || r.created_at)} · versão {r.termo_version || "v1"}
                      </div>
                      <button
                        onClick={() => downloadTermoPdf(r.id, r.code)}
                        data-testid={`rsvp-pdf-${r.code}`}
                        className="mt-3 inline-flex items-center gap-2 mono text-[10px] uppercase tracking-[0.24em] text-[color:var(--alta-red)] hover:text-[color:var(--alta-red-dark)] border-b border-[color:var(--alta-red)]/30 hover:border-[color:var(--alta-red-dark)] pb-1"
                      >
                        Baixar PDF do termo ↓
                      </button>
                    </div>
                  )}
                </div>
                <Cell label="Restrições" value={r.dietary || "—"} />
                <Cell label="Roupa" value={r.clothing_size} />
                <Cell label="Calçado" value={r.shoe_size} />
                <div className="md:col-span-2 mono text-[10px] uppercase tracking-[0.24em] text-neutral-500 md:text-right">
                  {new Date(r.created_at).toLocaleDateString("pt-BR")}
                </div>
              </article>
            ))}
            {!rsvps.length && (
              <div className="py-24 text-center text-neutral-500 font-light">
                Aguardando confirmações.
              </div>
            )}
          </div>
        )}

        {tab === "prereg" && <PreRegistrations />}

        {tab === "content" && <ContentEditor />}
      </div>
    </div>
  );
}

function Stat({ k, v, tone, testid }) {
  const color =
    tone === "red"
      ? "text-[color:var(--alta-red)]"
      : tone === "soft"
      ? "text-[color:var(--alta-red-soft)]"
      : "text-neutral-900";
  return (
    <div data-testid={testid}>
      <div className={`serif text-[64px] leading-none ${color}`}>{k}</div>
      <div className="mt-3 hairline" />
      <div className="mt-3 mono text-[10px] uppercase tracking-[0.28em] text-neutral-600">{v}</div>
    </div>
  );
}

function FunnelBadge({ activated, confirmed }) {
  if (confirmed) {
    return (
      <span className="mono text-[10px] uppercase tracking-[0.24em] px-3 py-1 border border-[color:var(--alta-red)] text-[color:var(--alta-red)] bg-[color:var(--alta-red)]/5">
        Confirmada · termo ✓
      </span>
    );
  }
  if (activated) {
    return (
      <span className="mono text-[10px] uppercase tracking-[0.24em] px-3 py-1 border border-[color:var(--alta-red-soft)] text-[color:var(--alta-red-soft)]">
        Convite ativado
      </span>
    );
  }
  return (
    <span className="mono text-[10px] uppercase tracking-[0.24em] px-3 py-1 border border-neutral-400 text-neutral-500">
      Aguardando
    </span>
  );
}

function Cell({ label, value }) {
  return (
    <div className="md:col-span-2">
      <div className="mono text-[10px] uppercase tracking-[0.28em] text-neutral-500">{label}</div>
      <div className="mt-2 text-[15px] text-neutral-900">{value}</div>
    </div>
  );
}

// ------------------- CONTENT EDITOR ------------------
const SECTIONS = [
  { key: "hero", label: "Hero (primeira dobra)" },
  { key: "manifesto", label: "Manifesto" },
  { key: "cast", label: "Elenco (The Cast)" },
  { key: "shotlist", label: "Cortes (Shot List)" },
  { key: "scenarios", label: "Cenários (cabeçalho + 4 locações)" },
  { key: "bagagem", label: "Bagagem (5 cenários + 3 preparações)" },
  { key: "timeline", label: "Programação (3 dias)" },
  { key: "gate", label: "Portão de acesso" },
  { key: "footer", label: "Rodapé" },
  { key: "guest", label: "Painel do convidado" },
  { key: "reality", label: "Reality · Casa Alta (página /reality + pré-lista)" },
];

const FIELD_LABELS = {
  // hero
  pill1: "Pill · parte 1", pill2: "Pill · parte 2", pill3: "Pill · parte 3",
  eyebrow: "Eyebrow (linha superior)",
  title1: "Título · texto 1", title2: "Título · destaque colorido",
  subline_prefix: "Subline · texto", subline_accent: "Subline · destaque",
  data_tag: "Info · Data (label)", data_value: "Info · Data (valor)", data_sub: "Info · Data (subtítulo)",
  local_tag: "Info · Local (label)", local_value: "Info · Local (valor)", local_sub: "Info · Local (subtítulo)",
  formato_tag: "Info · Formato (label)", formato_value: "Info · Formato (valor)", formato_sub: "Info · Formato (subtítulo)",
  cta_primary: "CTA principal", cta_secondary: "CTA secundário", cta_email: "E-mail do CTA secundário",
  scroll_cue: "Texto do scroll",
  // manifesto
  date_note: "Data (mono)",
  body_pre: "Corpo · início", body_accent: "Corpo · destaque", body_post: "Corpo · final",
  stat1_k: "Stat 1 · número", stat1_v: "Stat 1 · legenda",
  stat2_k: "Stat 2 · número", stat2_v: "Stat 2 · legenda",
  stat3_k: "Stat 3 · número", stat3_v: "Stat 3 · legenda",
  // cast
  title_accent: "Título · destaque colorido",
  body: "Corpo/descrição",
  footnote: "Rodapé (linha discreta)",
  // shotlist
  title: "Título",
  // scenarios individuais
  s1_tag: "Cenário 1 · Tag", s1_title: "Cenário 1 · Título", s1_body: "Cenário 1 · Corpo",
  s2_tag: "Cenário 2 · Tag", s2_title: "Cenário 2 · Título", s2_body: "Cenário 2 · Corpo",
  s3_tag: "Cenário 3 · Tag", s3_title: "Cenário 3 · Título", s3_body: "Cenário 3 · Corpo",
  s4_tag: "Cenário 4 · Tag", s4_title: "Cenário 4 · Título", s4_body: "Cenário 4 · Corpo",
  // shot list itens (6 cards de entregáveis)
  i1_n: "Card 1 · Número", i1_label: "Card 1 · Título", i1_sub: "Card 1 · Subtítulo",
  i2_n: "Card 2 · Número", i2_label: "Card 2 · Título", i2_sub: "Card 2 · Subtítulo",
  i3_n: "Card 3 · Número", i3_label: "Card 3 · Título", i3_sub: "Card 3 · Subtítulo",
  i4_n: "Card 4 · Número", i4_label: "Card 4 · Título", i4_sub: "Card 4 · Subtítulo",
  i5_n: "Card 5 · Número", i5_label: "Card 5 · Título", i5_sub: "Card 5 · Subtítulo",
  i6_n: "Card 6 · Número", i6_label: "Card 6 · Título", i6_sub: "Card 6 · Subtítulo",
  // timeline
  d1_day: "Dia 1 · Nome", d1_date: "Dia 1 · Data", d1_theme: "Dia 1 · Tema/subtítulo",
  d2_day: "Dia 2 · Nome", d2_date: "Dia 2 · Data", d2_theme: "Dia 2 · Tema/subtítulo",
  d3_day: "Dia 3 · Nome", d3_date: "Dia 3 · Data", d3_theme: "Dia 3 · Tema/subtítulo",
  // bagagem — cabeçalho e 5 cenários
  c1_title: "Cenário 1 · Nome (ex: Piscina & Trampolim)",
  c1_tag: "Cenário 1 · Tag (ex: Cortes de moda · Reels)",
  c1_looks: "Cenário 1 · Roupas & Looks (use • para bullet, uma por linha)",
  c1_extras: "Cenário 1 · Acessórios & Extras",
  c2_title: "Cenário 2 · Nome", c2_tag: "Cenário 2 · Tag",
  c2_looks: "Cenário 2 · Roupas & Looks", c2_extras: "Cenário 2 · Acessórios & Extras",
  c3_title: "Cenário 3 · Nome", c3_tag: "Cenário 3 · Tag",
  c3_looks: "Cenário 3 · Roupas & Looks", c3_extras: "Cenário 3 · Acessórios & Extras",
  c4_title: "Cenário 4 · Nome", c4_tag: "Cenário 4 · Tag",
  c4_looks: "Cenário 4 · Roupas & Looks", c4_extras: "Cenário 4 · Acessórios & Extras",
  c5_title: "Cenário 5 · Nome", c5_tag: "Cenário 5 · Tag",
  c5_looks: "Cenário 5 · Roupas & Looks", c5_extras: "Cenário 5 · Acessórios & Extras",
  // bagagem — preparação pessoal (bloco escuro)
  p_eyebrow: "Preparação · Eyebrow", p_title: "Preparação · Título", p_body: "Preparação · Corpo",
  p1_title: "Preparação 1 · Título", p1_body: "Preparação 1 · Corpo",
  p2_title: "Preparação 2 · Título", p2_body: "Preparação 2 · Corpo",
  p3_title: "Preparação 3 · Título", p3_body: "Preparação 3 · Corpo",
  // scenarios/gate
  title_pre: "Título · texto", input_label: "Rótulo do input", button: "Texto do botão", confidential: "Selo lateral",
  // footer
  tagline: "Subtítulo",
  curadoria_label: "Bloco 'Curadoria' · label", curadoria_value: "Bloco 'Curadoria' · valor",
  local_label: "Bloco 'Locação' · label",
  // guest — login screen (CodeEntry)
  login_pill_a: "Login · Badge esquerdo (ex: 'Área reservada')",
  login_pill_b: "Login · Badge direito (ex: 'Convite pessoal')",
  login_eyebrow: "Login · Eyebrow com data e local",
  login_title_pre: "Login · Título antes do destaque (ex: 'Sua ')",
  login_title_accent: "Login · Palavra em destaque vermelho (ex: 'chave')",
  login_body_pre: "Login · Corpo do texto (antes do destaque)",
  login_body_accent: "Login · Corpo · palavra em vermelho (ex: 'Alta Agency')",
  login_body_post: "Login · Corpo (final da frase)",
  login_field_label: "Login · Label do campo de código",
  login_placeholder: "Login · Placeholder do campo",
  login_button: "Login · Texto do botão",
  login_button_loading: "Login · Botão (loading)",
  login_disclaimer: "Login · Disclaimer sob o botão",
  login_help: "Login · Linha de ajuda (email de contato)",

  // guest — welcome hero
  welcome_pill_aguarda: "Boas-vindas · Badge quando não confirmou",
  welcome_pill_confirmada: "Boas-vindas · Badge quando já confirmou",
  welcome_edition_tag: "Boas-vindas · Tag da edição (ex: 'Casa Alta 2026')",
  welcome_eyebrow: "Boas-vindas · Eyebrow (data e local)",
  welcome_greeting: "Boas-vindas · Saudação (ex: 'Olá,')",
  welcome_body_pre: "Boas-vindas · Corpo antes do destaque",
  welcome_body_accent: "Boas-vindas · Corpo (destaque colorido)",
  welcome_body_post: "Boas-vindas · Corpo depois do destaque",
  welcome_cta_confirm: "Boas-vindas · Texto do botão (nova confirmação)",
  welcome_cta_edit: "Boas-vindas · Texto do botão (já confirmou, editar)",
  welcome_cta_note: "Boas-vindas · Nota ao lado do botão",
  welcome_scroll_cue: "Boas-vindas · Cue de rolagem (ex: 'Role para explorar')",

  // guest — sub-nav labels
  subnav_inicio: "Sub-menu · Label 'Início'",
  subnav_cenarios: "Sub-menu · Label 'Cenários'",
  subnav_elenco: "Sub-menu · Label 'Elenco'",
  subnav_cortes: "Sub-menu · Label 'Cortes'",
  subnav_bagagem: "Sub-menu · Label 'Bagagem'",
  subnav_agenda: "Sub-menu · Label 'Agenda'",
  subnav_confirmar: "Sub-menu · Botão CTA (ex: 'Confirmar vaga →')",

  // guest — top nav
  nav_badge: "Menu topo · Badge (ex: 'Convite ativado')",
  nav_logout: "Menu topo · Label do botão sair",
  nav_confirm_short: "Menu topo · Texto do botão Confirmar",

  // guest — info strip
  info_localizacao_label: "Info · Localização · label", info_localizacao_value: "Info · Localização · valor",
  info_checkin_label: "Info · Check-in · label", info_checkin_value: "Info · Check-in · valor",
  info_checkout_label: "Info · Check-out · label", info_checkout_value: "Info · Check-out · valor",
  info_dress_label: "Info · Dress code · label", info_dress_value: "Info · Dress code · valor",

  // guest — timeline headers
  timeline_eyebrow: "Programação · eyebrow", timeline_title: "Programação · título",

  // guest — RSVP form
  rsvp_eyebrow: "RSVP · eyebrow",
  rsvp_title_pre: "RSVP · título (antes do destaque)",
  rsvp_title_accent: "RSVP · título (destaque)",
  rsvp_body: "RSVP · corpo explicativo",
  rsvp_already_note: "RSVP · aviso 'você já confirmou'",
  rsvp_field_name: "RSVP · label 'Nome completo'",
  rsvp_field_social: "RSVP · label 'Instagram/TikTok'",
  rsvp_field_social_placeholder: "RSVP · placeholder Instagram",
  rsvp_field_diet: "RSVP · label 'Restrições alimentares'",
  rsvp_field_diet_placeholder: "RSVP · placeholder restrições",
  rsvp_field_clothing: "RSVP · label 'Tamanho de roupa'",
  rsvp_field_clothing_placeholder: "RSVP · placeholder roupa",
  rsvp_field_shoe: "RSVP · label 'Tamanho de calçado'",
  rsvp_field_shoe_placeholder: "RSVP · placeholder calçado",
  rsvp_termo_pill: "RSVP · pill 'Termo obrigatório'",
  rsvp_signature_label: "RSVP · label da assinatura",
  rsvp_signature_placeholder: "RSVP · placeholder da assinatura",
  rsvp_consent_pre: "RSVP · consentimento (antes do destaque)",
  rsvp_consent_accent: "RSVP · consentimento (destaque)",
  rsvp_consent_post: "RSVP · consentimento (final)",
  rsvp_submit_new: "RSVP · botão (nova confirmação)",
  rsvp_submit_edit: "RSVP · botão (atualizar confirmação)",
  rsvp_submit_loading: "RSVP · botão (enviando)",
  rsvp_submit_hint: "RSVP · dica se termo não aceito",

  // guest — closing + footer
  closing_eyebrow: "Encerramento · eyebrow",
  closing_line_pre: "Encerramento · texto",
  closing_line_accent: "Encerramento · destaque",
  footer_line: "Rodapé logado · linha esquerda",
  footer_logout: "Rodapé logado · botão sair",

  // guest — timeline day items (Programação · dia N · item M)
  d1_i1_time: "Dia 1 · Atividade 1 · hora", d1_i1_title: "Dia 1 · Atividade 1 · título", d1_i1_desc: "Dia 1 · Atividade 1 · descrição",
  d1_i2_time: "Dia 1 · Atividade 2 · hora", d1_i2_title: "Dia 1 · Atividade 2 · título", d1_i2_desc: "Dia 1 · Atividade 2 · descrição",
  d1_i3_time: "Dia 1 · Atividade 3 · hora", d1_i3_title: "Dia 1 · Atividade 3 · título", d1_i3_desc: "Dia 1 · Atividade 3 · descrição",
  d1_i4_time: "Dia 1 · Atividade 4 · hora (opcional)", d1_i4_title: "Dia 1 · Atividade 4 · título", d1_i4_desc: "Dia 1 · Atividade 4 · descrição",
  d1_i5_time: "Dia 1 · Atividade 5 · hora (opcional)", d1_i5_title: "Dia 1 · Atividade 5 · título", d1_i5_desc: "Dia 1 · Atividade 5 · descrição",
  d1_i6_time: "Dia 1 · Atividade 6 · hora (opcional)", d1_i6_title: "Dia 1 · Atividade 6 · título", d1_i6_desc: "Dia 1 · Atividade 6 · descrição",
  d2_i1_time: "Dia 2 · Atividade 1 · hora", d2_i1_title: "Dia 2 · Atividade 1 · título", d2_i1_desc: "Dia 2 · Atividade 1 · descrição",
  d2_i2_time: "Dia 2 · Atividade 2 · hora", d2_i2_title: "Dia 2 · Atividade 2 · título", d2_i2_desc: "Dia 2 · Atividade 2 · descrição",
  d2_i3_time: "Dia 2 · Atividade 3 · hora", d2_i3_title: "Dia 2 · Atividade 3 · título", d2_i3_desc: "Dia 2 · Atividade 3 · descrição",
  d2_i4_time: "Dia 2 · Atividade 4 · hora", d2_i4_title: "Dia 2 · Atividade 4 · título", d2_i4_desc: "Dia 2 · Atividade 4 · descrição",
  d2_i5_time: "Dia 2 · Atividade 5 · hora", d2_i5_title: "Dia 2 · Atividade 5 · título", d2_i5_desc: "Dia 2 · Atividade 5 · descrição",
  d2_i6_time: "Dia 2 · Atividade 6 · hora (opcional)", d2_i6_title: "Dia 2 · Atividade 6 · título", d2_i6_desc: "Dia 2 · Atividade 6 · descrição",
  d3_i1_time: "Dia 3 · Atividade 1 · hora", d3_i1_title: "Dia 3 · Atividade 1 · título", d3_i1_desc: "Dia 3 · Atividade 1 · descrição",
  d3_i2_time: "Dia 3 · Atividade 2 · hora", d3_i2_title: "Dia 3 · Atividade 2 · título", d3_i2_desc: "Dia 3 · Atividade 2 · descrição",
  d3_i3_time: "Dia 3 · Atividade 3 · hora", d3_i3_title: "Dia 3 · Atividade 3 · título", d3_i3_desc: "Dia 3 · Atividade 3 · descrição",
  d3_i4_time: "Dia 3 · Atividade 4 · hora (opcional)", d3_i4_title: "Dia 3 · Atividade 4 · título", d3_i4_desc: "Dia 3 · Atividade 4 · descrição",
  d3_i5_time: "Dia 3 · Atividade 5 · hora (opcional)", d3_i5_title: "Dia 3 · Atividade 5 · título", d3_i5_desc: "Dia 3 · Atividade 5 · descrição",
  d3_i6_time: "Dia 3 · Atividade 6 · hora (opcional)", d3_i6_title: "Dia 3 · Atividade 6 · título", d3_i6_desc: "Dia 3 · Atividade 6 · descrição",
};

// Normalize an @handle for dedup comparison
function normalizeHandle(h) {
  return String(h || "")
    .toLowerCase()
    .replace(/^@+/, "")
    .replace(/[\s/]+/g, "")
    .trim();
}

function RSVPImportModal({ existing, onClose, onImport }) {
  const [rsvps, setRsvps] = React.useState(null); // null = loading
  const [selected, setSelected] = React.useState(new Set());

  React.useEffect(() => {
    api
      .get("/admin/rsvps")
      .then((r) => setRsvps(Array.isArray(r.data) ? r.data : []))
      .catch((err) => {
        toast.error(formatApiError(err));
        setRsvps([]);
      });
  }, []);

  // Set of normalized handles already present in the cast (to flag "já no Elenco")
  const existingSet = React.useMemo(() => {
    const s = new Set();
    (existing || []).forEach((c) => s.add(normalizeHandle(c.handle)));
    return s;
  }, [existing]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllAvailable = () => {
    const next = new Set();
    (rsvps || []).forEach((r) => {
      const norm = normalizeHandle(r.social_handle);
      if (!existingSet.has(norm)) next.add(r.id);
    });
    setSelected(next);
  };

  const doImport = () => {
    if (!selected.size) return;
    const chosen = (rsvps || []).filter((r) => selected.has(r.id));
    const newCreators = chosen.map((r) => {
      // Ensure handle starts with @ for consistency with cast display
      const rawHandle = String(r.social_handle || "").trim();
      const handle = rawHandle
        ? rawHandle.startsWith("@") ? rawHandle : `@${rawHandle.replace(/^@+/, "")}`
        : "";
      return {
        id: `rsvp-${r.id}`,
        src: "",
        handle: handle || (r.full_name || "").split(/\s+/)[0] || "@convidada",
        vibe: r.full_name || "Confirmada",
        reveal: false,
      };
    });
    onImport(newCreators);
  };

  const loading = rsvps === null;
  const available = (rsvps || []).filter((r) => !existingSet.has(normalizeHandle(r.social_handle)));
  const alreadyImported = (rsvps || []).length - available.length;

  return (
    <div
      data-testid="rsvp-import-modal"
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#F9F8F6] w-full max-w-[820px] max-h-[85vh] flex flex-col border border-neutral-300 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 md:p-8 border-b border-neutral-200 flex items-start justify-between gap-4">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--alta-red)]">
              Importar do RSVP
            </div>
            <h3 className="mt-2 serif text-[26px] md:text-[32px] leading-tight text-neutral-900">
              Confirmadas prontas para virar card do Elenco.
            </h3>
            <p className="mt-3 text-[13px] leading-[1.7] text-neutral-600 max-w-[60ch] font-light">
              Selecione quem já confirmou o RSVP. Elas serão adicionadas como cards no Elenco com
              nome e @ já preenchidos — depois é só fazer upload da foto de cada uma.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="rsvp-import-close"
            className="mono text-[10px] uppercase tracking-[0.24em] text-neutral-500 hover:text-neutral-900 px-3 py-2"
          >
            Fechar ✕
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8" data-testid="rsvp-import-list">
          {loading && (
            <div className="py-16 text-center mono text-[11px] uppercase tracking-[0.24em] text-neutral-500">
              Carregando confirmações...
            </div>
          )}
          {!loading && !rsvps.length && (
            <div className="py-16 text-center text-neutral-500">
              <div className="serif text-[22px] text-neutral-900">Ainda sem confirmações.</div>
              <div className="mt-3 text-[13px]">Assim que as criadoras preencherem o RSVP, elas aparecem aqui.</div>
            </div>
          )}
          {!loading && rsvps.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-4 mono text-[10px] uppercase tracking-[0.24em] text-neutral-500">
                <span>
                  {available.length} disponível{available.length === 1 ? "" : "eis"}
                  {alreadyImported > 0 && ` · ${alreadyImported} já no Elenco`}
                </span>
                {available.length > 0 && (
                  <button
                    type="button"
                    onClick={selectAllAvailable}
                    data-testid="rsvp-import-select-all"
                    className="text-[color:var(--alta-red)] hover:text-[color:var(--alta-red-dark)] border-b border-[color:var(--alta-red)]/40 hover:border-[color:var(--alta-red-dark)]"
                  >
                    Selecionar todas disponíveis
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {rsvps.map((r) => {
                  const norm = normalizeHandle(r.social_handle);
                  const already = existingSet.has(norm);
                  const isSel = selected.has(r.id);
                  return (
                    <label
                      key={r.id}
                      data-testid={`rsvp-import-row-${r.code}`}
                      className={`flex items-center gap-4 p-4 border transition-colors cursor-pointer ${
                        already
                          ? "border-neutral-200 bg-neutral-100 opacity-60 cursor-not-allowed"
                          : isSel
                          ? "border-[color:var(--alta-red)] bg-[color:var(--alta-red)]/5"
                          : "border-neutral-300 bg-white hover:border-neutral-500"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => !already && toggle(r.id)}
                        disabled={already}
                        className="h-4 w-4 accent-[color:var(--alta-red)]"
                        data-testid={`rsvp-import-check-${r.code}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="serif text-[17px] text-neutral-900 leading-tight">
                            {r.full_name || "—"}
                          </span>
                          {r.social_handle && (
                            <span className="mono text-[11px] uppercase tracking-[0.16em] text-neutral-500">
                              {String(r.social_handle).startsWith("@") ? r.social_handle : `@${r.social_handle}`}
                            </span>
                          )}
                          <span className="mono text-[9px] uppercase tracking-[0.24em] text-[color:var(--alta-red)] border border-[color:var(--alta-red)]/40 px-2 py-0.5">
                            {r.code}
                          </span>
                        </div>
                        {already && (
                          <div className="mt-1 mono text-[9px] uppercase tracking-[0.24em] text-neutral-500">
                            já no Elenco
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 md:p-8 border-t border-neutral-200 flex items-center justify-between gap-4 bg-white">
          <span className="mono text-[10px] uppercase tracking-[0.24em] text-neutral-600">
            {selected.size} selecionada{selected.size === 1 ? "" : "s"}
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost !py-2 !px-4 !text-[10px]"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={doImport}
              disabled={!selected.size}
              data-testid="rsvp-import-confirm"
              className="btn-red-primary !py-2 !px-4 !text-[10px] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Adicionar ao Elenco →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CastPhotosEditor({ values, onChange, onReplaceCreators }) {
  const creators = React.useMemo(() => getCreators(values), [values]);
  const [showImport, setShowImport] = React.useState(false);

  const updateAt = (idx, patch) => {
    const next = creators.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    onReplaceCreators(next);
  };

  const removeAt = (idx) => {
    if (!window.confirm(`Remover a criadora #${idx + 1}?`)) return;
    onReplaceCreators(creators.filter((_, i) => i !== idx));
  };

  const move = (idx, delta) => {
    const j = idx + delta;
    if (j < 0 || j >= creators.length) return;
    const next = [...creators];
    [next[idx], next[j]] = [next[j], next[idx]];
    onReplaceCreators(next);
  };

  const addCreator = ({ reveal = false } = {}) => {
    const id = `c${Date.now()}`;
    const next = [
      ...creators,
      reveal
        ? { id, src: "", handle: "@?????", vibe: "Revelação · em breve", reveal: true }
        : { id, src: "", handle: "@handle", vibe: "Vibe", reveal: false },
    ];
    onReplaceCreators(next);
  };

  const uploadFile = async (idx, file) => {
    if (!file) return;
    if (file.size > 2_000_000) {
      toast.error("Foto grande demais (limite 2 MB). Comprima antes de subir — recomendamos JPG/PNG em 1200×1600 px, qualidade 80%.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      updateAt(idx, { src: reader.result, reveal: false });
      toast.success(`Foto ${idx + 1} carregada — lembre de salvar`);
    };
    reader.readAsDataURL(file);
  };

  const isEmbedded = (src) => typeof src === "string" && src.startsWith("data:");

  return (
    <div className="p-6 border-t border-neutral-200 space-y-6" data-testid="cast-photos-editor">
      {/* Text fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {["eyebrow", "title1", "title2", "title_accent", "footnote"].map((k) => (
          <label key={k} className="block">
            <span className="mono text-[9px] uppercase tracking-[0.24em] text-neutral-500">
              {FIELD_LABELS[k] || k}
            </span>
            <input
              value={values[k] || ""}
              onChange={(e) => onChange(k, e.target.value)}
              className="mt-2 w-full border border-neutral-300 bg-white p-3 text-[13px] focus:outline-none focus:border-[color:var(--alta-red)]"
            />
          </label>
        ))}
        <label className="block md:col-span-2">
          <span className="mono text-[9px] uppercase tracking-[0.24em] text-neutral-500">Corpo/descrição</span>
          <textarea
            value={values.body || ""}
            onChange={(e) => onChange("body", e.target.value)}
            rows={4}
            className="mt-2 w-full border border-neutral-300 bg-white p-3 text-[13px] leading-[1.55] focus:outline-none focus:border-[color:var(--alta-red)]"
          />
        </label>
      </div>

      {/* Dynamic creators grid */}
      <div className="border-t border-neutral-200 pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--alta-red)]">
            {creators.length} {creators.length === 1 ? "criadora" : "criadoras"} no elenco
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowImport(true)}
              data-testid="cast-import-rsvp"
              className="mono text-[10px] uppercase tracking-[0.24em] text-neutral-800 hover:text-[color:var(--alta-red)] px-3 py-2 border border-neutral-400 hover:border-[color:var(--alta-red)] bg-white"
              title="Importar criadoras que já confirmaram no RSVP"
            >
              ↓ Importar do RSVP
            </button>
            <button
              type="button"
              onClick={() => addCreator({ reveal: false })}
              data-testid="cast-add-creator"
              className="btn-red-primary !text-[10px] !px-3 !py-2"
            >
              + Adicionar criadora
            </button>
            <button
              type="button"
              onClick={() => addCreator({ reveal: true })}
              data-testid="cast-add-reveal"
              className="mono text-[10px] uppercase tracking-[0.24em] text-neutral-600 hover:text-[color:var(--alta-red)] px-3 py-2 border border-neutral-300"
            >
              + Card surpresa
            </button>
          </div>
        </div>

        {showImport && (
          <RSVPImportModal
            existing={creators}
            onClose={() => setShowImport(false)}
            onImport={(newCreators) => {
              onReplaceCreators([...creators, ...newCreators]);
              setShowImport(false);
              toast.success(
                `${newCreators.length} ${
                  newCreators.length === 1 ? "criadora adicionada" : "criadoras adicionadas"
                } — agora faça upload das fotos e clique em Salvar.`
              );
            }}
          />
        )}

        {creators.length === 0 && (
          <div className="py-12 text-center border border-dashed border-neutral-300 text-[13px] text-neutral-500">
            Nenhuma criadora ainda. Clique em <em className="text-[color:var(--alta-red)]">+ Adicionar criadora</em>.
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {creators.map((c, i) => (
            <div key={c.id || i} className="border border-neutral-300 bg-white p-4" data-testid={`cast-slot-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="mono text-[9px] uppercase tracking-[0.28em] text-neutral-500">
                  Foto {i + 1} {isEmbedded(c.src) && <span className="text-[color:var(--alta-red)]">(subida)</span>}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    data-testid={`cast-up-${i}`}
                    className="w-6 h-6 flex items-center justify-center border border-neutral-300 text-neutral-600 hover:text-[color:var(--alta-red)] hover:border-[color:var(--alta-red)] disabled:opacity-30 disabled:hover:text-neutral-600 disabled:hover:border-neutral-300"
                    title="Mover para cima"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => move(i, 1)}
                    disabled={i === creators.length - 1}
                    data-testid={`cast-down-${i}`}
                    className="w-6 h-6 flex items-center justify-center border border-neutral-300 text-neutral-600 hover:text-[color:var(--alta-red)] hover:border-[color:var(--alta-red)] disabled:opacity-30 disabled:hover:text-neutral-600 disabled:hover:border-neutral-300"
                    title="Mover para baixo"
                  >
                    ↓
                  </button>
                </div>
              </div>
              <div className="relative aspect-[3/4] bg-neutral-100 overflow-hidden mb-3">
                {c.reveal || !c.src ? (
                  <div className="absolute inset-0 flex items-center justify-center text-[10px] uppercase tracking-[0.28em] text-neutral-400 mono">
                    {c.reveal ? "Card surpresa" : "Sem foto"}
                  </div>
                ) : (
                  <img src={c.src} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              <label className="block cursor-pointer btn-red-primary !text-[10px] !px-3 !py-2 w-full text-center mb-2" data-testid={`cast-upload-${i}`}>
                {c.src && !c.reveal ? "Substituir" : "Enviar foto"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => uploadFile(i, e.target.files?.[0])}
                />
              </label>
              <input
                value={c.handle || ""}
                onChange={(e) => updateAt(i, { handle: e.target.value })}
                placeholder="@handle"
                className="mt-2 w-full border border-neutral-300 p-2 text-[12px] focus:outline-none focus:border-[color:var(--alta-red)]"
                data-testid={`cast-handle-${i}`}
              />
              <input
                value={c.vibe || ""}
                onChange={(e) => updateAt(i, { vibe: e.target.value })}
                placeholder="Vibe / descrição"
                className="mt-2 w-full border border-neutral-300 p-2 text-[12px] focus:outline-none focus:border-[color:var(--alta-red)]"
                data-testid={`cast-vibe-${i}`}
              />
              <label className="mt-2 flex items-center gap-2 text-[11px] text-neutral-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!c.reveal}
                  onChange={(e) => updateAt(i, { reveal: e.target.checked })}
                  className="h-4 w-4 accent-[color:var(--alta-red)]"
                  data-testid={`cast-reveal-${i}`}
                />
                Card surpresa (esconde foto, mostra “Reveal em breve”)
              </label>
              <button
                type="button"
                onClick={() => removeAt(i)}
                data-testid={`cast-remove-${i}`}
                className="mt-3 w-full mono text-[10px] uppercase tracking-[0.24em] text-red-600 hover:bg-red-50 py-2 border border-red-200"
              >
                Remover criadora
              </button>
            </div>
          ))}
        </div>
        <p className="mt-4 mono text-[10px] uppercase tracking-[0.24em] text-neutral-500">
          Sem limite de fotos. Formato recomendado: JPG/PNG em 3:4 (retrato), até 2 MB.
          Depois de alterar, clique em <em className="italic text-[color:var(--alta-red)]"> Salvar e publicar</em>.
        </p>
      </div>
    </div>
  );
}


function ContentEditor() {
  const { reload } = useContent();
  const [values, setValues] = useState(DEFAULT_CONTENT);
  const [expanded, setExpanded] = useState("hero");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site/content").then((r) => setValues(mergeContent(r.data || {}))).catch(() => {});
  }, []);

  const setField = (section, key, val) =>
    setValues((v) => ({ ...v, [section]: { ...v[section], [key]: val } }));

  const replaceCastCreators = (nextCreators) =>
    setValues((v) => {
      // When switching to the array model, clear the legacy p1..p6_* keys
      const cast = { ...v.cast, creators: nextCreators };
      for (let n = 1; n <= 6; n++) {
        delete cast[`p${n}_src`];
        delete cast[`p${n}_handle`];
        delete cast[`p${n}_vibe`];
        delete cast[`p${n}_reveal`];
      }
      return { ...v, cast };
    });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site/content", values);
      await reload();
      toast.success("Textos salvos e publicados no site");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    if (window.confirm("Resetar TODOS os textos para o modelo padrão?")) {
      setValues(DEFAULT_CONTENT);
      toast.info("Modelo padrão restaurado — clique em Salvar para publicar.");
    }
  };

  return (
    <div data-testid="content-editor" className="space-y-4">
      <div className="flex items-center justify-between border border-neutral-300 bg-white/60 p-6">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--alta-red)]">
            Editor de textos do site
          </div>
          <p className="mt-2 text-[13px] text-neutral-600 max-w-[70ch]">
            Cada mudança aqui é <em>publicada em produção</em> ao clicar em Salvar. O site
            público (landing) e o painel do convidado passam a exibir os novos textos imediatamente.
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={reset} data-testid="content-reset" className="btn-ghost !py-3 !px-5">Resetar</button>
          <button onClick={save} disabled={saving} data-testid="content-save" className="btn-red-primary !py-3 !px-6">
            {saving ? "Salvando..." : "Salvar e publicar"}
          </button>
        </div>
      </div>

      {SECTIONS.map((sec) => {
        const open = expanded === sec.key;
        const fields = Object.keys(values[sec.key] || {});
        return (
          <div key={sec.key} className="border border-neutral-200 bg-white/60" data-testid={`section-${sec.key}`}>
            <button
              onClick={() => setExpanded(open ? "" : sec.key)}
              className="w-full flex items-center justify-between p-5 text-left hover:bg-[#F3F1EA] transition-colors"
            >
              <span className="mono text-[11px] uppercase tracking-[0.28em] text-neutral-900">{sec.label}</span>
              <span className="mono text-[10px] text-neutral-500">{open ? "—" : "+"}</span>
            </button>
            {open && sec.key === "cast" && (
              <CastPhotosEditor
                values={values.cast}
                onChange={(k, v) => setField("cast", k, v)}
                onReplaceCreators={replaceCastCreators}
              />
            )}
            {open && sec.key === "reality" && (
              <RealityEditor
                values={values.reality}
                onChange={(k, v) => setField("reality", k, v)}
                onReplaceEpisodes={(eps) => setField("reality", "episodes", eps)}
                onReplaceCast={(c) => setField("reality", "cast", c)}
              />
            )}
            {open && sec.key !== "cast" && sec.key !== "reality" && (
              <div className="p-6 border-t border-neutral-200 grid grid-cols-1 md:grid-cols-2 gap-5">
                {fields.map((k) => {
                  const isBody = /body|post|desc|description|looks|extras/i.test(k);
                  return (
                    <label key={k} className={`block ${isBody ? "md:col-span-2" : ""}`}>
                      <span className="mono text-[9px] uppercase tracking-[0.24em] text-neutral-500">
                        {FIELD_LABELS[k] || k}
                      </span>
                      {isBody ? (
                        <textarea
                          value={values[sec.key][k] || ""}
                          onChange={(e) => setField(sec.key, k, e.target.value)}
                          rows={4}
                          className="mt-2 w-full border border-neutral-300 bg-white p-3 text-[13px] leading-[1.55] text-neutral-900 focus:outline-none focus:border-[color:var(--alta-red)] font-[Manrope]"
                        />
                      ) : (
                        <input
                          value={values[sec.key][k] || ""}
                          onChange={(e) => setField(sec.key, k, e.target.value)}
                          className="mt-2 w-full border border-neutral-300 bg-white p-3 text-[13px] text-neutral-900 focus:outline-none focus:border-[color:var(--alta-red)]"
                        />
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
